import { useAxios } from '../utils/axiosConfig';
import { useNotification } from "../contexts/NotificationContext";
import config from '../contexts/ConfigContext';

export interface BatchModel {
    Id: number;
    Title: string;
    Subtitle: string;
    Total : number;
    Date: string;
    ShowButtonRelease: boolean;
    ShowButtonResume: boolean;
    ShowButtonStart: boolean;
    ChannelDescription: string;
}

export interface BatchGroupModel {
    GroupId: string;
    Name: string;
    NumberOfItems: number;
    NumberOfDocuments: number;
    Batches: BatchModel[];
}

export interface FilterBatchGroupModel {
    startDate: Date;
    endDate: Date;
}

export interface FilterBatchModel {
    startDate: Date;
    endDate: Date;
    groupId: string;
}

export interface DocumentModel{
    Id: number;
    Author: string;
    ThumbnailSrc: string;
    Title: string;
    Content: string;
    ImagesUrls: string[];
    Source: string;
    PublishDate: Date;
    Link: string;
    Status: number;
    StatusLabel: string;
    Url: string;
    ProviderId: string;
    UniqueId: string;
    AudioToText: string;
    MediaMentions: string[];
    Mentions: string[];
    SponsoredMentions: string[];
    Hashtags: string[];
    AuthorComments: string;
    DocumentChanged: boolean;
}

export interface LabelingModel{
    PreviousDocumentId? :number;
    CurrentDocument: DocumentModel;
    NextDocumentId? :number;
    DocumentPositionLabel: string;
}


export class BatchService {

    axios = useAxios();

    // Define the notification hook
    notification = useNotification();

    // Call the API to get the filters for the current user and project
    async GetBatchGroup(filter: FilterBatchGroupModel): Promise<BatchGroupModel[]> {
        try {
            const response = await this.axios.get(`${config.API_LABELING_URL}/api/Batches/GetBatchGroups?startDate=${filter.startDate.toDateString()}&endDate=${filter.endDate.toDateString()}`);
            return response.data;
        } catch (error) {
            // Handle error
            this.notification.showNotification('Error fetching filters for GetBatchGroup: ' + error, 'error');
            console.error('Error fetching filters for GetBatchGroup:', error);
            return [];
        }
    } 
    
    async GetBatches(filter: FilterBatchModel): Promise<BatchGroupModel>{
        try {
            const response = await this.axios.get(`${config.API_LABELING_URL}/api/Batches/GetBatches?startDate=${filter.startDate.toDateString()}&endDate=${filter.endDate.toDateString()}&sourceType=${filter.groupId}`);
            return response.data;
        } catch (error) {
            // Handle error
            this.notification.showNotification('Error fetching filters for GetBatches: ' + error, 'error');
            console.error('Error fetching filters for GetBatches:', error);
            return {GroupId: '', Name: '', NumberOfItems: 0, NumberOfDocuments: 0, Batches: []};
        }
    }

    async StartBatch(batchId: number, sourceTypeId: number ): Promise<number> {
        try {
            const response = await this.axios.get(`${config.API_LABELING_URL}/api/Batches/StartBatch?batchId=${batchId}&sourceTypeId=${sourceTypeId}`);
            return response.data;
        } catch (error) {
            // Handle error
            this.notification.showNotification('Error fetching data for StartBatch: ' + error, 'error');
            console.error('Error fetching data for StartBatch:', error);
            return -1;
        }
    }

    async StartNextBatch (currentBatchId: number): Promise<number> {
        try {
            const response = await this.axios.get(`${config.API_LABELING_URL}/api/Batches/StartNextBatch?currentBatchId=${currentBatchId}`);
            return response.data;
        } catch (error: any) {
            // Handle error
            if(error?.response?.status === 404){
                this.notification.showNotification(error.response.tite, 'warning');
            }
            if(error?.response?.status === 422){
                this.notification.showNotification(error.response.data?.detail, 'warning');
            }
            else{
            this.notification.showNotification('Error fetching data for StartNextBatch: ' + error, 'error');
            console.error('Error fetching data for StartNextBatch:', error);
            }
            throw error;
        }
    }

    async GetCurrentDocument(batchId: number, documentId?: number): Promise<LabelingModel> {
        try {
            const response = await this.axios.get(`${config.API_LABELING_URL}/api/Batches/GetCurrentDocument?batchId=${batchId}${(documentId !== undefined) ?'&documentId='+documentId : ''}`);
            return response.data;
        } catch (error) {
            // Handle error
            this.notification.showNotification('Error fetching current document: ' + error, 'error');
            console.error('Error fetching current document:', error);
            throw error;
        }
    }

    async WorkBatch(workBatch: LabelingModel): Promise<LabelingModel> {
        try {
            const response = await this.axios.post(config.API_LABELING_URL + '/api/Batches/WorkBatch/', workBatch);
            return response.data;
        } catch (error) {
            // Handle error
            this.notification.showNotification('Error fetching data for WorkBatch: ' + error, 'error');
            console.error('Error fetching data for WorkBatch:', error);
            throw error;
        }
    }

    async ResumeBatch(batchId: number, sourceType: string): Promise<LabelingModel> 
    {
        try {
            const response = await this.axios.get(`${config.API_LABELING_URL}/api/Batches/ResumeBatch?batchId=${batchId}&sourceType=${sourceType}`);            return response.data;
        } catch (error) {
            // Handle error
            this.notification.showNotification('Error fetching data for ResumeBatch: ' + error, 'error');
            console.error('Error fetching data for ResumeBatch:', error);
            throw error;
        }
    }

    async ReleaseBatch(batchId: number): Promise<boolean> {
        try {
            const response = await this.axios.get(`${config.API_LABELING_URL}/api/Batches/ReleaseBatch?batchId=${batchId}`);
            return response.data;
        } catch (error) {
            // Handle error
            this.notification.showNotification('Error call api ReleaseBatch: ' + error, 'error');
            console.error('Error call api ReleaseBatch:', error);
            return false;
        }
    }
}